import $ from 'jquery';
import { __ } from '@wordpress/i18n';
import 'owl.carousel';
import 'owl.carousel/dist/assets/owl.carousel.css';
import 'owl.carousel/dist/assets/owl.theme.default.css';
import '../sass/style.scss';

const initFaqAccordion = () => {
    const faqBlocks = document.querySelectorAll('.ls-faq');

    faqBlocks.forEach( ( block ) => {
        const items = block.querySelectorAll('.ls-faq__item');

        items.forEach( ( item, index ) => {
            const trigger = item.querySelector('.ls-faq__trigger');
            const panel = item.querySelector('.ls-faq__panel');

            if ( ! trigger || ! panel ) {
                return;
            }

            panel.setAttribute('hidden', '');
            trigger.setAttribute('aria-expanded', 'false');

            trigger.addEventListener('click', () => {
                const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

                if ( ! block.hasAttribute('data-allow-multiple') ) {
                    items.forEach( ( sibling ) => {
                        if ( sibling === item ) {
                            return;
                        }

                        const siblingTrigger = sibling.querySelector('.ls-faq__trigger');
                        const siblingPanel = sibling.querySelector('.ls-faq__panel');

                        if ( siblingTrigger && siblingPanel ) {
                            siblingTrigger.setAttribute('aria-expanded', 'false');
                            siblingPanel.classList.remove('is-open');
                            siblingPanel.setAttribute('hidden', '');
                        }
                    } );
                }

                if ( isExpanded ) {
                    trigger.setAttribute('aria-expanded', 'false');
                    panel.classList.remove('is-open');
                    panel.setAttribute('hidden', '');
                } else {
                    trigger.setAttribute('aria-expanded', 'true');
                    panel.classList.add('is-open');
                    panel.removeAttribute('hidden');
                }
            } );

            if ( index === 0 ) {
                trigger.setAttribute('aria-expanded', 'true');
                panel.classList.add('is-open');
                panel.removeAttribute('hidden');
            }
        } );
    } );
};

const initPrimaryMenu = () => {
    const menus = document.querySelectorAll('.ls-primary-menu');

    menus.forEach( ( menu, index ) => {
        if ( menu.dataset.lsPrimaryInit === 'true' ) {
            return;
        }

        const toggle = menu.querySelector('.ls-primary-menu__toggle');
        const nav = menu.querySelector('.ls-primary-menu__nav');

        if ( ! toggle || ! nav ) {
            return;
        }

        menu.dataset.lsPrimaryInit = 'true';

        const navId = nav.id || menu.dataset.menuId || `ls-primary-menu-${ index }`;

        nav.id = navId;
        toggle.setAttribute('aria-controls', navId);

        const rawBreakpoint = parseInt( menu.dataset.breakpoint || '780', 10 );
        const breakpoint = Number.isFinite( rawBreakpoint ) ? rawBreakpoint : 780;
        const mediaQuery = window.matchMedia( `(max-width: ${ breakpoint }px)` );

        let isExpanded = null;

        const setState = ( expand ) => {
            if ( expand === isExpanded ) {
                return;
            }

            isExpanded = expand;

            toggle.setAttribute('aria-expanded', expand ? 'true' : 'false' );
            menu.dataset.lsPrimaryExpanded = expand ? 'true' : 'false';
            menu.classList.toggle('is-open', expand );

            if ( expand ) {
                nav.classList.add( 'is-open' );
                nav.removeAttribute( 'hidden' );
            } else {
                nav.classList.remove( 'is-open' );
                nav.setAttribute( 'hidden', '' );
            }
        };

        const syncWithViewport = ( matchesMobile ) => {
            setState( ! matchesMobile );
        };

        syncWithViewport( mediaQuery.matches );

        const handleMediaChange = ( event ) => {
            syncWithViewport( event.matches );
        };

        if ( typeof mediaQuery.addEventListener === 'function' ) {
            mediaQuery.addEventListener( 'change', handleMediaChange );
        } else if ( typeof mediaQuery.addListener === 'function' ) {
            mediaQuery.addListener( handleMediaChange );
        }

        toggle.addEventListener( 'click', () => {
            const currentlyExpanded = toggle.getAttribute( 'aria-expanded' ) === 'true';
            setState( ! currentlyExpanded );
        } );

        nav.addEventListener( 'keyup', ( event ) => {
            if ( event.key === 'Escape' || event.key === 'Esc' ) {
                setState( false );
                toggle.focus();
            }
        } );

        nav.querySelectorAll( 'a' ).forEach( ( link ) => {
            link.addEventListener( 'click', () => {
                if ( mediaQuery.matches ) {
                    setState( false );
                }
            } );
        } );

        const handleDocumentClick = ( event ) => {
            if ( ! mediaQuery.matches ) {
                return;
            }

            if ( ! menu.contains( event.target ) ) {
                setState( false );
            }
        };

        document.addEventListener( 'click', handleDocumentClick );
    } );
};

const initMegaMenu = () => {
    const menus = document.querySelectorAll('.ls-menu-special');

    menus.forEach( ( menu ) => {
        if ( menu.dataset.lsMegaInit === 'true' ) {
            return;
        }

        menu.dataset.lsMegaInit = 'true';

        const closeItem = ( item ) => {
            const trigger = item.querySelector('.ls-menu-special__link');
            const panel = item.querySelector('.ls-menu-special__mega, .ls-menu-special__list-content');

            if ( trigger ) {
                trigger.setAttribute('aria-expanded', 'false');
            }

            if ( panel ) {
                panel.setAttribute('hidden', '');
            }

            item.classList.remove('is-open');
        };

        const items = menu.querySelectorAll('.ls-menu-special__item.is-mega, .ls-menu-special__item.has-list');

        items.forEach( ( item, index ) => {
            const trigger = item.querySelector('.ls-menu-special__link');
            const panel = item.querySelector('.ls-menu-special__mega, .ls-menu-special__list-content');

            if ( ! trigger || ! panel ) {
                return;
            }

            let closeTimeout;

            const panelId = panel.id || `ls-mega-panel-${ index }-${ Math.random().toString(36).slice(2, 8) }`;

            panel.id = panelId;
            panel.setAttribute('hidden', '');
            trigger.setAttribute('aria-controls', panelId);
            trigger.setAttribute('aria-haspopup', 'true');
            trigger.setAttribute('aria-expanded', 'false');

            const cancelClose = () => {
                if ( closeTimeout ) {
                    window.clearTimeout( closeTimeout );
                }
            };

            const open = () => {
                menu.querySelectorAll('.ls-menu-special__item.is-open').forEach( ( openItem ) => {
                    if ( openItem !== item ) {
                        closeItem( openItem );
                    }
                } );

                cancelClose();
                item.classList.add('is-open');
                trigger.setAttribute('aria-expanded', 'true');
                panel.removeAttribute('hidden');
            };

            const close = () => {
                cancelClose();
                closeTimeout = window.setTimeout( () => {
                    closeItem( item );
                }, 120 );
            };

            item.addEventListener('mouseenter', open );
            item.addEventListener('mouseleave', close );

            trigger.addEventListener('focus', open );
            trigger.addEventListener('blur', ( event ) => {
                if ( ! item.contains( event.relatedTarget ) ) {
                    close();
                }
            } );

            panel.addEventListener('focusin', open );
            panel.addEventListener('focusout', ( event ) => {
                if ( ! panel.contains( event.relatedTarget ) ) {
                    close();
                }
            } );

            closeItem( item );
        } );
    } );
};

const initSliders = () => {
    const jQueryRef = window?.jQuery || $;

    if ( ! jQueryRef || ! jQueryRef.fn || typeof jQueryRef.fn.owlCarousel !== 'function' ) {
        return;
    }

    const navText = [
        `<span class="ls-slider__nav-icon" aria-hidden="true">‹</span><span class="screen-reader-text">${ __( 'Previous slide', 'lubstarterfse' ) }</span>`,
        `<span class="ls-slider__nav-icon" aria-hidden="true">›</span><span class="screen-reader-text">${ __( 'Next slide', 'lubstarterfse' ) }</span>`,
    ];

    const baseOptions = {
        items: 1,
        loop: true,
        margin: 24,
        nav: true,
        dots: true,
        navText,
        smartSpeed: 450,
    };

    const ensureCarousel = ( element, overrides = {} ) => {
        if ( ! element || element.dataset.lsSliderInit === 'true' ) {
            return;
        }

        const $element = jQueryRef( element );

        if ( ! $element.length ) {
            return;
        }

        const options = { ...baseOptions, ...overrides };

        element.dataset.lsSliderInit = 'true';
        $element.addClass( 'owl-carousel' );
        $element.owlCarousel( options );
    };

    document.querySelectorAll( '.ls-slider__track' ).forEach( ( track ) => {
        ensureCarousel( track );
    } );

    document.querySelectorAll( '.ls-slider-dynamic__track' ).forEach( ( track ) => {
        ensureCarousel( track, {
            autoplay: true,
            autoplayTimeout: 6000,
            autoplayHoverPause: true,
        } );
    } );
};

const activateTab = ( tab, tabs, panels ) => {
    const controls = tab.getAttribute('aria-controls');
    const panel = controls ? document.getElementById(controls) : null;

    tabs.forEach( ( currentTab ) => {
        currentTab.classList.remove('is-active');
        currentTab.setAttribute('aria-selected', 'false');
        currentTab.setAttribute('tabindex', '-1');
    } );

    panels.forEach( ( currentPanel ) => {
        currentPanel.classList.remove('is-active');
        currentPanel.setAttribute('hidden', '');
    } );

    tab.classList.add('is-active');
    tab.setAttribute('aria-selected', 'true');
    tab.setAttribute('tabindex', '0');

    if ( panel ) {
        panel.classList.add('is-active');
        panel.removeAttribute('hidden');
    }
};

const handleTabKeyboardNavigation = ( event, tabs ) => {
    const currentIndex = tabs.indexOf(event.currentTarget);

    if ( currentIndex === -1 ) {
        return;
    }

    const focusTab = ( index ) => {
        const tabToFocus = tabs[index];

        if ( tabToFocus ) {
            tabToFocus.focus();
        }
    };

    switch ( event.key ) {
        case 'ArrowRight':
        case 'ArrowDown':
            event.preventDefault();
            focusTab( ( currentIndex + 1 ) % tabs.length );
            break;
        case 'ArrowLeft':
        case 'ArrowUp':
            event.preventDefault();
            focusTab( ( currentIndex - 1 + tabs.length ) % tabs.length );
            break;
        default:
            break;
    }
};

const initTabs = () => {
    const tabContainers = document.querySelectorAll('.ls-tabs');

    tabContainers.forEach( ( container ) => {
        const tabs = Array.from( container.querySelectorAll('.ls-tabs__nav-item') );
        const panels = Array.from( container.querySelectorAll('.ls-tabs__panel') );

        if ( ! tabs.length || ! panels.length ) {
            return;
        }

        tabs.forEach( ( tab ) => {
            tab.addEventListener('click', ( event ) => {
                event.preventDefault();
                activateTab( tab, tabs, panels );
            } );

            tab.addEventListener('keydown', ( event ) => handleTabKeyboardNavigation( event, tabs ) );
        } );

        const initialTab = tabs.find( ( candidate ) => candidate.classList.contains('is-active') ) || tabs[0];

        if ( initialTab ) {
            activateTab( initialTab, tabs, panels );
        }
    } );
};

const initSiteHeaderMobileMenu = () => {
    const toggle = document.getElementById('site-menu-toggle');
    const drawer = document.getElementById('site-mobile-nav');
    const header = document.getElementById('site-header') || document.querySelector('.site-header');

    if ( ! toggle || ! drawer ) {
        return;
    }

    const setMenuState = ( isOpen ) => {
        toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        if ( isOpen ) {
            drawer.removeAttribute('hidden');
            header?.classList.add('is-menu-open');
        } else {
            drawer.setAttribute('hidden', '');
            header?.classList.remove('is-menu-open');
        }
    };

    toggle.addEventListener('click', ( event ) => {
        event.stopPropagation();
        const currentlyExpanded = toggle.getAttribute('aria-expanded') === 'true';
        setMenuState( ! currentlyExpanded );
    });

    drawer.querySelectorAll('a').forEach(( link ) => {
        link.addEventListener('click', () => {
            setMenuState( false );
        });
    });

    document.addEventListener('click', ( event ) => {
        if ( header && ! header.contains( event.target ) && ! drawer.contains( event.target ) ) {
            setMenuState( false );
        }
    });

    document.addEventListener('keydown', ( event ) => {
        if ( event.key === 'Escape' || event.key === 'Esc' ) {
            setMenuState( false );
        }
    });

    const mediaQuery = window.matchMedia('(min-width: 901px)');
    const handleResize = ( event ) => {
        if ( event.matches ) {
            setMenuState( false );
        }
    };

    if ( typeof mediaQuery.addEventListener === 'function' ) {
        mediaQuery.addEventListener('change', handleResize);
    } else if ( typeof mediaQuery.addListener === 'function' ) {
        mediaQuery.addListener(handleResize);
    }
};

document.addEventListener('DOMContentLoaded', () => {
    initSiteHeaderMobileMenu();
    initPrimaryMenu();
    initMegaMenu();
    initFaqAccordion();
    initTabs();
    initSliders();
} );

window.addEventListener('load', () => {
    initSliders();
});

console.info('[wp-buchalter theme is live]');

